import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form } from 'antd';

import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type {
  BasePriceConfiguration,
  PriceDifferentiationModeType,
} from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import { PRICE_DIFFERENTIATION_MODE } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';

import { markFormDirty } from '../../../hook/useValidationStore';

type PriceButtonsProps = {
  priceDifferentiationMode: PriceDifferentiationModeType;
  disableNightToggle?: boolean;
};

const getModeMap = () => ({
  nightDay: {
    [PRICE_DIFFERENTIATION_MODE.NONE]: PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME,
    [PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME]: PRICE_DIFFERENTIATION_MODE.NONE,
    [PRICE_DIFFERENTIATION_MODE.WITH_CHILD]:
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME,
    [PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME]:
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD,
  },
  adultChild: {
    [PRICE_DIFFERENTIATION_MODE.NONE]: PRICE_DIFFERENTIATION_MODE.WITH_CHILD,
    [PRICE_DIFFERENTIATION_MODE.WITH_CHILD]: PRICE_DIFFERENTIATION_MODE.NONE,
    [PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME]:
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME,
    [PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME]:
      PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME,
  },
});

export const PriceButtons = ({
  priceDifferentiationMode,
  disableNightToggle = false,
}: PriceButtonsProps) => {
  const form = Form.useFormInstance();
  const isAlwaysIncludedWithNight = Form.useWatch(
    'isAlwaysIncludedWithNight',
    form
  );
  const allowedPax = Form.useWatch(['allowedPaxTypes'], form);

  const hasAdultOnly =
    allowedPax?.length === 1 && allowedPax[0] === PAX_TYPE.ADULT;
  const hasChildOnly =
    allowedPax?.length === 1 && allowedPax[0] === PAX_TYPE.CHILD;
  const hasBothPaxTypes = !hasAdultOnly && !hasChildOnly;

  const hasDayPrice =
    priceDifferentiationMode ===
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME ||
    priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME;

  const hasAdultChildPrice =
    hasBothPaxTypes &&
    (priceDifferentiationMode ===
      PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME ||
      priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD);

  const handleTogglePriceDifferentiation = (
    buttonType: 'nightDay' | 'adultChild'
  ) => {
    const modeMap = getModeMap();
    const newMode = modeMap[buttonType][priceDifferentiationMode];

    const newHasDay =
      newMode === PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME ||
      newMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME;
    const newHasChild =
      newMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD ||
      newMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME;

    // Drop the price fields the new mode no longer allows: antd keeps unmounted
    // field values, and a lingering value fails validation with no editable
    // input left to clear it.
    const clearForbidden = (
      config: BasePriceConfiguration = {}
    ): BasePriceConfiguration => ({
      ...config,
      daytimePrice: newHasDay ? config.daytimePrice : undefined,
      childPrice: newHasChild ? config.childPrice : undefined,
      childDaytimePrice:
        newHasDay && newHasChild ? config.childDaytimePrice : undefined,
    });

    const priceConfiguration =
      form.getFieldValue(['openingConfiguration', 'priceConfiguration']) ?? {};

    markFormDirty();
    form.setFieldsValue({
      openingConfiguration: {
        priceConfiguration:
          priceConfiguration.type === OPENING_CONFIGURATION_TYPE.DAILY
            ? {
                priceDifferentiationMode: newMode,
                days: Object.fromEntries(
                  Object.entries(priceConfiguration.days ?? {}).map(
                    ([day, config]) => [
                      day,
                      clearForbidden(config as BasePriceConfiguration),
                    ]
                  )
                ),
              }
            : {
                priceDifferentiationMode: newMode,
                configuration: clearForbidden(priceConfiguration.configuration),
              },
      },
    });
  };

  return (
    <Form.Item
      name={[
        'openingConfiguration',
        'priceConfiguration',
        'priceDifferentiationMode',
      ]}
      noStyle
    >
      <div className="price-configuration-buttons">
        {!isAlwaysIncludedWithNight && (
          <Button
            icon={hasDayPrice ? <DeleteOutlined /> : <PlusOutlined />}
            onClick={() => handleTogglePriceDifferentiation('nightDay')}
            type={hasDayPrice ? 'dashed' : 'default'}
            danger={hasDayPrice}
            disabled={disableNightToggle}
          >
            {`${hasDayPrice ? 'Delete' : 'Add'} night/day price`}
          </Button>
        )}

        {hasBothPaxTypes && (
          <Button
            icon={hasAdultChildPrice ? <DeleteOutlined /> : <PlusOutlined />}
            onClick={() => handleTogglePriceDifferentiation('adultChild')}
            type={hasAdultChildPrice ? 'dashed' : 'default'}
            danger={hasAdultChildPrice}
          >
            {`${hasAdultChildPrice ? 'Delete' : 'Add'} adult/child price`}
          </Button>
        )}
      </div>
    </Form.Item>
  );
};
